import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { translations, type Lang, type Translation } from "./translations";

interface Ctx {
  lang: Lang;
  setLang: (l: Lang) => void;
  t: Translation;
}

const LanguageContext = createContext<Ctx | undefined>(undefined);

const isEnPath = (path: string) => path === "/en" || path.startsWith("/en/");

/** Map a path on one language to its equivalent on the other. */
export const mapPathToLang = (path: string, target: Lang): string => {
  // Strip trailing slash except root
  const p = path.replace(/\/$/, "") || "/";
  const enToNl: Record<string, string> = {
    "/en": "/",
    "/en/for-agents": "/voor-makelaars",
    "/en/for-vendors": "/voor-leveranciers",
    "/en/home-advisor": "/wooncoach",
    "/en/about": "/about",
    "/en/faq": "/faq",
    "/en/contact": "/contact",
    "/en/pricing": "/#pricing",
    "/en/privacy": "/privacy",
    "/en/terms": "/terms",
    "/en/cookies": "/cookies",
    "/en/security": "/security",
    "/en/dpa": "/dpa",
  };
  const nlToEn: Record<string, string> = {
    "/": "/en",
    "/voor-makelaars": "/en/for-agents",
    "/voor-leveranciers": "/en/for-vendors",
    "/wooncoach": "/en/home-advisor",
    "/about": "/en/about",
    "/faq": "/en/faq",
    "/veelgestelde-vragen": "/en/faq",
    "/contact": "/en/contact",
    "/privacy": "/en/privacy",
    "/terms": "/en/terms",
    "/cookies": "/en/cookies",
    "/security": "/en/security",
    "/dpa": "/en/dpa",
  };
  if (target === "nl") return enToNl[p] ?? (p.replace(/^\/en/, "") || "/");
  return nlToEn[p] ?? `/en${p}`;
};

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const urlLang: Lang = isEnPath(location.pathname) ? "en" : "nl";

  const [lang, setLangState] = useState<Lang>(urlLang);

  useEffect(() => {
    setLangState(urlLang);
    try { localStorage.setItem("tribeo-lang", urlLang); } catch {}
  }, [urlLang]);

  const setLang = (l: Lang) => {
    if (l === lang) return;
    const newPath = mapPathToLang(location.pathname, l);
    if (newPath.startsWith("/#")) {
      navigate(l === "en" ? "/en" : "/");
    } else {
      navigate(newPath);
    }
  };

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  return (
    <LanguageContext.Provider value={{ lang, setLang, t: translations[lang] }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLang = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLang must be used within LanguageProvider");
  return ctx;
};
